<script setup lang="ts">
import { computed } from "vue";
import { Navigation2 } from "@lucide/vue";
import WeatherIconSimple from "./WeatherIconSimple.vue";
import PrecipitationChips from "./PrecipitationChips.vue";
import { useUnitConversion } from "@/composables/useUnitConversion";
import type { TemperatureUnit } from "@/stores/settings.store";

const props = defineProps<{
  label: string;
  isDay: boolean;
  weatherCode: number;
  temperature: number;
  condition: string;
  pop?: number;
  rain?: number;
  ice?: number;
  snow?: number;
  windSpeed?: number;
  windDirection?: number;
  isToday?: boolean;
  wallMode?: boolean;
  tabletMode?: boolean;
  unit: TemperatureUnit;
}>();

const { convertSpeed, labels } = useUnitConversion(() => props.unit);

const periodClasses = computed(() => ({
  period: props.wallMode
    ? "min-w-0 p-1 pb-2 short-wall:pt-[3px] compact-wall:pb-1.5"
    : "px-3 py-3 md:px-2 xl:px-3",
  heading: props.wallMode
    ? "mb-0.5 min-h-4 short-wall:mb-0 short-wall:min-h-3.5"
    : "mb-2 min-h-9",
  weather: props.wallMode
    ? "mb-0.5 min-h-[58px] gap-[5px] [--weather-pop-size:13px] [--weather-symbol-size:clamp(36px,25cqw,44px)] short-wall:mb-0 short-wall:[--weather-symbol-size:44px] compact-wall:min-h-[40px] compact-wall:[--weather-symbol-size:32px]"
    : "mb-2 gap-2 md:mb-1 xl:mb-2",
  temperature: props.wallMode
    ? "text-[clamp(22px,23cqw,30px)] leading-none"
    : "text-3xl",
  condition: props.wallMode
    ? "mb-[3px] block min-h-[34px] overflow-visible text-[13px] leading-[17px] short-wall:mb-0 short-wall:min-h-8 short-wall:leading-4"
    : "mb-2 line-clamp-2 text-xs leading-tight xl:block",
}));
</script>

<template>
  <div
    class="forecast-period flex min-h-min flex-col items-center [&>*]:shrink-0"
    :class="periodClasses.period"
  >
    <div
      class="period-heading flex shrink-0 items-center justify-center"
      :class="periodClasses.heading"
    >
      <div
        v-if="isDay"
        class="day-pill rounded-full font-semibold text-text-primary"
        :class="
          wallMode
            ? 'bg-transparent p-0 text-xs leading-4 short-wall:leading-3.5'
            : [
                'bg-app-elevated px-4 py-1.5 text-sm md:px-3 md:py-1 md:text-xs xl:px-4 xl:py-1.5 xl:text-sm',
                {
                  'shadow-[inset_0_0_0_1.5px_var(--accent-color)]': isToday,
                },
              ]
        "
      >
        {{ label }}
      </div>
      <p
        v-else
        class="text-center text-[10px] tracking-wider text-text-muted uppercase"
      >
        {{ label }}
      </p>
    </div>

    <div
      class="period-weather flex items-center justify-center"
      :class="periodClasses.weather"
    >
      <WeatherIconSimple
        :weather-code="weatherCode"
        :is-day="isDay"
        :size="wallMode ? 40 : tabletMode ? 44 : 48"
        :pop="pop ?? 0"
      />
      <span
        class="period-temperature font-bold text-text-primary"
        :class="periodClasses.temperature"
      >
        {{ temperature }}°
      </span>
    </div>

    <p
      class="period-condition text-center font-semibold text-text-primary"
      :class="periodClasses.condition"
    >
      {{ condition }}
    </p>

    <div
      v-if="isDay && windSpeed"
      class="period-wind mb-2 items-center justify-center gap-1"
      :class="wallMode ? 'hidden' : 'hidden xl:flex'"
    >
      <Navigation2
        class="h-4 w-4 text-text-secondary"
        :style="{ transform: `rotate(${(windDirection || 0) + 180}deg)` }"
        aria-hidden="true"
      />
      <span class="text-xs text-text-secondary">
        {{ convertSpeed(windSpeed) }} {{ labels.speed }}
      </span>
    </div>

    <PrecipitationChips
      :rain="rain"
      :ice="ice"
      :snow="snow"
      :unit="unit"
      :wall-mode="wallMode"
    />
  </div>
</template>
